import { createContext, useContext, ReactNode, useEffect, useState } from 'react';
import {
  getDeviceCapabilities,
  cacheDeviceCapabilities,
  getCachedDeviceCapabilities,
  getLightModeOverride,
  type DeviceCapabilities,
} from '@/lib/tvDetection';

interface PerformanceContextType extends DeviceCapabilities {
  lightModeEnabled: boolean;
  toggleLightMode: () => void;
}

const PerformanceContext = createContext<PerformanceContextType | undefined>(undefined);

export const PerformanceProvider = ({ children }: { children: ReactNode }) => {
  const [capabilities, setCapabilities] = useState<DeviceCapabilities>(() => {
    // Try to load from cache first for faster initial render
    const cached = getCachedDeviceCapabilities();
    return cached || {
      isSmartTV: false,
      isLowPowerDevice: false,
      supportsHover: true,
      memoryLimitMB: 2048,
      shouldUseLightMode: false,
    };
  });

  const [lightModeOverride, setLightModeOverride] = useState<boolean | null>(null);

  useEffect(() => {
    // Detect device capabilities on mount
    const detected = getDeviceCapabilities();
    setCapabilities(detected);
    cacheDeviceCapabilities(detected);

    // Check for manual override
    const override = getLightModeOverride();
    setLightModeOverride(override);

    // Log detection for debugging
    console.log('[Performance] Device detection results:', {
      userAgent: navigator.userAgent,
      isSmartTV: detected.isSmartTV,
      isLowPowerDevice: detected.isLowPowerDevice,
      lightModeEnabled: detected.shouldUseLightMode,
      screenWidth: window.screen.width,
      devicePixelRatio: window.devicePixelRatio,
    });
    
    if (detected.isSmartTV || detected.shouldUseLightMode) {
      console.log('[Performance] ⚡ Light mode enabled automatically for optimal performance');
    } else {
      console.log('[Performance] ✓ Full features enabled (not a TV/low-power device)');
    }
  }, []);

  const lightModeEnabled = lightModeOverride !== null 
    ? lightModeOverride 
    : capabilities.shouldUseLightMode;

  const toggleLightMode = () => {
    const newValue = !lightModeEnabled;
    setLightModeOverride(newValue);
    
    try {
      localStorage.setItem('light_mode_override', newValue ? 'true' : 'false');
      // Clear device capabilities cache so detection runs fresh
      localStorage.removeItem('device_capabilities');
    } catch (error) {
      console.warn('Failed to save light mode preference:', error);
    }
    
    // Force re-detection on next page load
    window.location.reload();
  };

  return (
    <PerformanceContext.Provider
      value={{
        ...capabilities,
        lightModeEnabled,
        toggleLightMode,
      }}
    >
      {children}
    </PerformanceContext.Provider>
  );
};

export const usePerformance = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformance must be used within PerformanceProvider');
  }
  return context;
};
